import {fetchQuery} from 'relay-runtime';
import DirectApi from '../../../directApi';
import environment from '../../../RelayEnvironment';
import Util from '../../../forecast-app/shared/util/util';
import FavoriteTaskMutation from '../../../mutations/favorite_task_mutation';
import AcquireReportLockMutation from '../../../mutations/reporting/AcquireReportLockMutation';
import DiscardReportDraftMutation from '../../../mutations/reporting/DiscardReportDraftMutation';
import EnterReportDraftMutation from '../../../mutations/reporting/EnterReportDraftMutation';
import HeartbeatReportLockMutation from '../../../mutations/reporting/HeartbeatReportLockMutation';
import PublishReportMutation from '../../../mutations/reporting/PublishReportMutation';
import ReleaseReportLockMutation from '../../../mutations/reporting/ReleaseReportLockMutation';
import SaveReportAsMutation from '../../../mutations/reporting/SaveReportAsMutation';
import UpdateSavedReportMutation from '../../../mutations/reporting/update_saved_report_mutation';
import {toRelayGlobalId} from '../../../containers/internal/nova-studio/shared/relayGlobalId';
import {Filter, ReportDefinitionV1Tasks} from '../contracts/report-definition.types';
import {GroupingToken} from '../ReportTable/useClientGrouping';
import {ReportDataResult} from './reportRowModel';
import ReportLockStatusQuery from './ReportLockStatusQuery';
import {
	LoadedReport,
	LockClaim,
	LockStatus,
	ReportDataSource,
	ReportDraftState,
	ReportExportTooLargeError,
	ReportLifecycleState,
	ViewerPermission,
} from './ReportDataSource';
import {FieldNode, mapFetchResponse} from './reportRowMapper';
import {buildReportExportInputs, ReportExportInputs} from './reportExportInputs';
import {toReportDefinition} from './storedDefinition';
import {
	buildSaveReportAsInput,
	buildUpdateSavedReportInput,
	SaveReportAsInputFields,
	SavedReportRef,
	UpdateSavedReportInputFields,
} from './savedReportInput';

/**
 * Real `ReportDataSource`. report-service is server-side only (the browser never calls it directly),
 * so this goes through the graphql Node server's thin authenticated passthrough express route
 * `POST /report/data/fetch` (see graphql `server.js` + `ReportServiceLogic.fetchTaskReportBuilderData`),
 * which forwards to report-service and returns the RAW hierarchical wrapped-node JSON that
 * `reportRowMapper` maps into `ReportRow`s.
 *
 * WHY an express passthrough and NOT a Relay field (the codebase's usual preference): the response is
 * a freeform, recursively-nested `{name,value}` blob defined by the report-service taxonomy, not by
 * our schema. Relay-modelling it would need a JSON scalar (loses all typing anyway) or a big
 * recursive type + fragments — real complexity for no benefit over the typed client-side mapper. The
 * route rides the same graphql-server host the browser already hits for `/report/*_export_csv`, so it
 * works in prod (ALB) with no new nginx/terraform routing. Auth is the standard credentialed
 * (cookie session) fetch — same as the export_csv callers.
 */
const FETCH_PATH = 'report/data/fetch';

/** Sibling passthrough returning a saved report's definition (see graphql `server.js`). The saved-report
 * Relay shape carries columns only as a flattened eye-options string, so re-opening a report reads the
 * definition directly rather than rebuilding the field tree in the client. */
const DEFINITION_PATH = 'report/definition';

/** Sibling passthrough returning the full (unpaginated) export as decoded CSV bytes (see graphql
 * `server.js` + report-service's ad-hoc export). Forwards the posted body verbatim to report-service,
 * so the body built here IS the backend contract. */
const EXPORT_PATH = 'report/data/export';

/** What report-service returns when an ordered export's result set exceeds its row ceiling. It fails
 * rather than truncating, because a short file that looks complete is a wrong answer the user cannot
 * detect — so this status means "narrow the report", never "try again". */
const EXPORT_TOO_LARGE_STATUS = 413;

/** The exact JSON body `EXPORT_PATH` forwards to report-service, named so the contract is checked
 * rather than inferred: an untyped object literal would let a mistyped `hierarchical` or a dropped
 * `orderBy` compile and only fail against the live backend. Extends `ReportExportInputs` so the
 * `{labels, dataProcessorInputs, orderBy}` half stays defined in one place. */
export interface ReportExportBody extends ReportExportInputs {
	filter: Filter;
	hierarchical: boolean;
}

/** Builds the export request body: `buildReportExportInputs`' `{labels, dataProcessorInputs, orderBy}`
 * plus the definition's canonical filter forwarded as-is, plus `hierarchical: true` — the
 * builder always wants the depth-first row order that lets a flat CSV reconstruct the task tree from
 * its id/parent-id columns (see `reportExportInputs.ts`). No taxonomy is passed: the data source has
 * none available, so labels fall back to `DEFAULT_FIELD_LABELS`/raw path as `buildReportExportInputs`
 * already handles. */
export function buildExportBody(
	definition: ReportDefinitionV1Tasks,
	grouping: GroupingToken[],
	hiddenColumnPaths?: readonly string[]
): ReportExportBody {
	const {labels, dataProcessorInputs, orderBy} = buildReportExportInputs(definition, grouping, null, hiddenColumnPaths);
	return {
		labels,
		dataProcessorInputs,
		orderBy,
		filter: definition.filter,
		hierarchical: true,
	};
}

/** Build the fetch request body from the report definition. `hierarchical: true` is REQUIRED to get the
 * nested / deliverable-tier / context-row response the builder table renders (false/null returns a
 * flat Standard-report array).
 *
 * The definition's `fields` are forwarded verbatim — they are already a concrete `{name, children}`
 * tree (no display-token expansion needed here). Duration columns need the project's estimation unit
 * to render, but the client does NOT request it: report-service guarantees `project.estimationUnit`
 * (+ `minutesPerEstimationPoint`) travels with any hierarchical response that carries a unit-bearing
 * column, so it is always present for the mapper without bloating the definition. */
export function buildFetchBody(
	definition: ReportDefinitionV1Tasks,
	page: {offset: number; limit: number}
): Record<string, unknown> {
	return {
		fields: definition.fields,
		filter: definition.filter,
		pagination: {offset: page.offset, limit: page.limit},
		hierarchical: true,
	};
}

/** The `savedReport` node both saved-report mutations return, narrowed to what a save needs back. */
type SavedReportPayload = {id: string; privateAccess: boolean | null} | null | undefined;

/** Mutation response keyed by the mutation's root field (`saveReportAs` / `updateSavedReport`). */
type SavedReportMutationResponse = Record<string, {savedReport?: SavedReportPayload} | undefined>;

/** A saved-report mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type SavedReportMutationModule = {commit: unknown};

/** Runs a saved-report mutation as a promise so the caller can await the save and react to a
 * failure.
 *
 * The default `Util.CommitMutation` failure path pops a global mutation-error modal; the report
 * builder reports a failed save on the surface itself and keeps the unsaved draft editable, so the
 * `onError` override deliberately does NOT delegate to that fallback. It also gives up the
 * wrapper's one automatic retry of a transient network failure — the surface's error state invites
 * the user to press Save again instead.
 *
 * A response that resolves without a `savedReport` id is treated as a failure: without an id a
 * follow-up save could not target the report and would silently create a duplicate. */
function commitSave(
	mutation: SavedReportMutationModule,
	input: SaveReportAsInputFields | UpdateSavedReportInputFields,
	selectSavedReport: (response: SavedReportMutationResponse) => SavedReportPayload
): Promise<SavedReportRef> {
	return new Promise<SavedReportRef>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			input,
			(response: SavedReportMutationResponse) => {
				const savedReport = selectSavedReport(response);
				if (!savedReport?.id) {
					reject(new Error('saved report mutation returned no report'));
					return;
				}
				resolve({id: savedReport.id, privateAccess: savedReport.privateAccess ?? true});
			},
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

/** The lifecycle payload each mutation returns, keyed by its root field. `draftState` arrives typed
 * as `ReportDraftState | null` by the generated Relay types, but the underlying GraphQL field is a
 * plain `String`, not an enum — report-service is free to add or misspell a value with no schema
 * change, so the raw payload is read as an unnarrowed string here and validated in `narrowDraftState`
 * before it reaches the union type. */
type LifecycleMutationResponse = Record<
	string,
	(Omit<ReportLifecycleState, 'draftState'> & {draftState?: string | null}) | undefined
>;

const KNOWN_DRAFT_STATES: ReadonlySet<string> = new Set(['PUBLISHED', 'DRAFT', 'UNPUBLISHED']);

/** Narrows a raw `draftState` string to the known union, mapping anything else — including a value
 * report-service might add later — to null rather than throwing. Consumers treat a null/unknown
 * state as "no reader isolation proven", the conservative reading: a publish that actually succeeded
 * must not be reported as a failure just because of an unrecognised state string. */
function narrowDraftState(draftState: string | null | undefined): ReportDraftState | null {
	return draftState != null && KNOWN_DRAFT_STATES.has(draftState) ? (draftState as ReportDraftState) : null;
}

/** A lifecycle mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type LifecycleMutationModule = {commit: unknown};

/** Runs a lifecycle mutation as a promise. Same reasoning as `commitSave`: the surface reports a
 * failed lifecycle action itself and keeps the editor where they are, so the default global
 * mutation-error modal is deliberately not delegated to. A response without a `draftState` and
 * without `locked` is treated as a failure, because the surface would otherwise show a state it
 * never received. */
function commitLifecycle(
	mutation: LifecycleMutationModule,
	reportId: string,
	rootField: string,
	lockToken?: string | null
): Promise<ReportLifecycleState> {
	return new Promise<ReportLifecycleState>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			// The token is omitted rather than sent null when this tab holds none: report-service reads a
			// presented token as "this caller participates in the lock protocol" and refuses the write when
			// it no longer names a live lock the caller holds.
			lockToken ? {reportId, lockToken} : {reportId},
			(response: LifecycleMutationResponse) => {
				const payload = response?.[rootField];
				if (!payload || (payload.draftState == null && !payload.locked)) {
					reject(new Error(`${rootField} returned no lifecycle state`));
					return;
				}
				resolve({
					draftState: narrowDraftState(payload.draftState),
					publishedAt: payload.publishedAt ?? null,
					locked: payload.locked === true,
					draftFilterWillRevert: payload.draftFilterWillRevert === true,
				});
			},
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

/** The lock holder/claim shape every lock query and mutation returns a subset of. Absent fields
 * (a heartbeat carries no `holder`/`lockedAt`) are read as null rather than assumed present. */
type LockPayload = {
	token?: string | null;
	locked?: boolean | null;
	heldByViewer?: boolean | null;
	holder?: {id: string; fullName: string; profilePictureId: string | null} | null;
	lockedAt?: string | null;
	expiresAt?: string | null;
};

function toLockStatus(payload: LockPayload | null | undefined): LockStatus {
	return {
		locked: payload?.locked === true,
		// Deliberately not coerced to a boolean: null means the server could not resolve whether the
		// viewer is the holder, and reading that as "not the viewer" is what made a user-service blip
		// look like a takeover to the tab that actually held the lock. Absent (a payload that never
		// selects the field) is the same unknown.
		heldByViewer: payload?.heldByViewer ?? null,
		holder: payload?.holder ?? null,
		lockedAt: payload?.lockedAt ?? null,
		expiresAt: payload?.expiresAt ?? null,
	};
}

function toLockClaim(payload: LockPayload | null | undefined): LockClaim {
	return {token: payload?.token ?? null, status: toLockStatus(payload)};
}

/** A lock mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type LockMutationModule = {commit: unknown};

/** Runs a lock-claiming mutation (acquire, heartbeat) as a promise that RESOLVES,
 * never rejects, on a lost race: report-service's 409 is already mapped by `ReportServiceLogic` to a
 * normal (non-error) payload with `token: null`, so a contended lock is read the same way here — only
 * a transport failure reaches `onError`. */
function commitLockClaim(mutation: LockMutationModule, input: Record<string, unknown>, rootField: string): Promise<LockClaim> {
	return new Promise<LockClaim>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			input,
			(response: Record<string, LockPayload | undefined>) => resolve(toLockClaim(response?.[rootField])),
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

/** A favorite-toggle mutation module (`{commit}`), as `Util.CommitMutation` takes it. */
type ToggleFavoriteMutationModule = {commit: unknown};

type ToggleFavoriteMutationResponse = {
	updateTask?: {taskEdge?: {node?: {id?: string; favoured?: boolean}}};
};

/** Runs the favorite-toggle mutation as a promise. Unlike `commitSave`/`commitLifecycle`, the caller
 * (`ReportBuilderContext`) has already flipped the row optimistically and only needs to know whether
 * to revert it — there is no lifecycle state or saved-report reference to extract on success. */
function commitToggleFavorite(mutation: ToggleFavoriteMutationModule, taskId: string, favoured: boolean): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		Util.CommitMutation(
			mutation,
			{ids: [toRelayGlobalId('Task', taskId)], favoured},
			(response: ToggleFavoriteMutationResponse) => {
				const node = response?.updateTask?.taskEdge?.node;
				if (!node) {
					reject(new Error('updateTask returned no task'));
					return;
				}
				// service-company can refuse the update (a locked task, a restricted project, a task
				// deleted between the fetch and the click) without the transport-level failure the
				// mutation otherwise catches: the graphql server re-fetches and returns the task
				// UNCHANGED instead of rethrowing, so `node` is present but `favoured` never actually
				// changed. Comparing against what was requested catches that silent refusal so the
				// caller still rolls back its optimistic flip.
				if (node.favoured !== favoured) {
					reject(new Error('updateTask did not apply the requested favoured state'));
					return;
				}
				resolve();
			},
			true,
			null,
			(transaction: unknown) => reject(new Error(String(transaction)))
		);
	});
}

export class HttpReportDataSource implements ReportDataSource {
	async fetchRows(definition: ReportDefinitionV1Tasks, page: {offset: number; limit: number}): Promise<ReportDataResult> {
		const response = await fetch(DirectApi.graphqlServerEndpoint(FETCH_PATH), {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify(buildFetchBody(definition, page)),
		});
		if (!response.ok) {
			throw new Error(`report/data/fetch failed: ${response.status}`);
		}
		const nodes = (await response.json()) as FieldNode[];
		return mapFetchResponse(Array.isArray(nodes) ? nodes : [], page.limit);
	}

	async loadDefinition(savedReportId: string): Promise<LoadedReport> {
		const response = await fetch(DirectApi.graphqlServerEndpoint(`${DEFINITION_PATH}/${savedReportId}`), {
			method: 'GET',
			credentials: 'include',
		});
		if (!response.ok) {
			throw new Error(`report/definition failed: ${response.status}`);
		}
		const stored = (await response.json()) as Record<string, unknown>;
		// Fails closed: an unreadable or absent permission hides edit affordances rather than offering
		// edits the backend will reject.
		const viewerPermission: ViewerPermission =
			stored.viewerPermission === 'OWNER' || stored.viewerPermission === 'EDITOR' ? stored.viewerPermission : 'VIEWER';
		return {
			definition: toReportDefinition(stored),
			// An unreadable or absent origin is treated as a standard report: the surface must not claim
			// an AI origin it cannot prove.
			source: stored.source === 'AI' ? 'AI' : 'STANDARD',
			promptText: typeof stored.promptText === 'string' && stored.promptText !== '' ? stored.promptText : null,
			// Strict `=== true`: anything else means the flag is not proven, and the banner must not claim
			// an edit it cannot back up.
			manuallyEdited: stored.manuallyEdited === true,
			viewerPermission,
			// Same narrowing the lifecycle mutations use: the stored state is a free-form string on the wire,
			// so anything outside the known union becomes null rather than reaching the union type unchecked.
			draftState: narrowDraftState(typeof stored.draftState === 'string' ? stored.draftState : null),
			publishedAt: typeof stored.publishedAt === 'string' && stored.publishedAt !== '' ? stored.publishedAt : null,
			// Deliberately not defaulted: a non-boolean means the stored value is unknown, and an update
			// overwrites this field unconditionally, so guessing would flip the report between private and
			// shared.
			privateAccess: typeof stored.privateAccess === 'boolean' ? stored.privateAccess : null,
			draftFilterWillRevert: stored.draftFilterWillRevert === true,
		};
	}

	saveDefinition(
		definition: ReportDefinitionV1Tasks,
		saved: SavedReportRef | null,
		promptText: string | null,
		manuallyEdited: boolean,
		lockToken?: string | null
	): Promise<SavedReportRef> {
		return saved === null
			? // A create has no report to lock yet, so there is no token to present and nothing to gate.
			  commitSave(
					SaveReportAsMutation,
					buildSaveReportAsInput(definition, promptText, manuallyEdited),
					response => response?.saveReportAs?.savedReport
			  )
			: commitSave(
					UpdateSavedReportMutation,
					buildUpdateSavedReportInput(saved, definition, manuallyEdited, lockToken),
					response => response?.updateSavedReport?.savedReport
			  );
	}

	async exportCsv(
		definition: ReportDefinitionV1Tasks,
		grouping: GroupingToken[],
		hiddenColumnPaths?: readonly string[]
	): Promise<void> {
		const response = await fetch(DirectApi.graphqlServerEndpoint(EXPORT_PATH), {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify(buildExportBody(definition, grouping, hiddenColumnPaths)),
		});
		if (!response.ok) {
			// A 413 is the one failure retrying cannot fix: report-service refuses an ordered export whose
			// result set exceeds its row ceiling, and the same request will exceed it again. Raised as its own
			// error type so the surface can say to narrow the report instead of telling the user to try again.
			if (response.status === EXPORT_TOO_LARGE_STATUS) {
				throw new ReportExportTooLargeError();
			}
			throw new Error(`report/data/export failed: ${response.status}`);
		}
		const blob = await response.blob();
		Util.exportToCSV(blob, definition.name);
	}

	enterDraft(savedReportId: string): Promise<ReportLifecycleState> {
		return commitLifecycle(EnterReportDraftMutation, savedReportId, 'enterReportDraft');
	}

	publish(savedReportId: string, lockToken?: string | null): Promise<ReportLifecycleState> {
		return commitLifecycle(PublishReportMutation, savedReportId, 'publishReport', lockToken);
	}

	discard(savedReportId: string, lockToken?: string | null): Promise<ReportLifecycleState> {
		return commitLifecycle(DiscardReportDraftMutation, savedReportId, 'discardReportDraft', lockToken);
	}

	lockStatus(savedReportId: string): Promise<LockStatus> {
		return fetchQuery(environment.getInstance(), ReportLockStatusQuery, {reportId: savedReportId})
			.toPromise()
			.then((data: {viewer?: {reportLockStatus?: LockPayload | null} | null} | null | undefined) =>
				toLockStatus(data?.viewer?.reportLockStatus)
			);
	}

	acquireLock(savedReportId: string): Promise<LockClaim> {
		return commitLockClaim(AcquireReportLockMutation, {reportId: savedReportId}, 'acquireReportLock');
	}

	heartbeatLock(savedReportId: string, token: string): Promise<LockClaim> {
		return commitLockClaim(HeartbeatReportLockMutation, {reportId: savedReportId, token}, 'heartbeatReportLock');
	}

	releaseLock(savedReportId: string, token: string): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			Util.CommitMutation(
				ReleaseReportLockMutation,
				{reportId: savedReportId, token},
				() => resolve(),
				true,
				null,
				(transaction: unknown) => reject(new Error(String(transaction)))
			);
		});
	}

	async toggleFavorite(taskId: string, favoured: boolean): Promise<void> {
		return commitToggleFavorite(FavoriteTaskMutation, taskId, favoured);
	}
}
